import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useStoreContext } from '../../contexts/StoreContext';
import AuthLayout from '../../components/auth/AuthLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { getPostLoginPath, isBackofficeUser, isReviterAdmin, isStoreManager } from '../../utils/permissions';

// `mode` define qual variação do login renderizamos:
//   - 'public' (default): /entrar do whitelabel — qualquer role pode logar.
//   - 'backoffice-reviter': /admin-reviter/login — só REVITER. Outros recebem erro.
//   - 'backoffice-store': /fornecedor/painel/login — só MANAGER + REVITER.
const LoginPage = ({ mode = 'public' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, logout, user: currentUser } = useAuth();
  const { store: currentStore } = useStoreContext();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const reason = searchParams.get('reason');
  const reasonMessage = reason === 'session-expired' ? 'Sua sessão expirou. Entre novamente pra continuar.' : null;

  const isBackoffice = mode !== 'public';
  const title = mode === 'backoffice-reviter'
    ? 'Reviter Backoffice'
    : mode === 'backoffice-store'
      ? 'Painel da loja'
      : 'Entrar';
  const subtitle = isBackoffice
    ? 'Acesso restrito a administradores.'
    : 'Acesse sua conta pra gerenciar pedidos, produtos e vendas.';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (!result.success) {
        setError(result.error || 'Credenciais inválidas.');
        return;
      }
      const user = result.user;
      // Modo backoffice só aceita REVITER (e MANAGER em /fornecedor/painel/login).
      if (mode === 'backoffice-reviter' && user?.role !== 'platform_admin') {
        await logout();
        setError('Este login é apenas para administradores Reviter.');
        return;
      }
      if (mode === 'backoffice-store' && !isBackofficeUser(user)) {
        await logout();
        setError('Este login é apenas para gerentes de loja.');
        return;
      }
      navigate(getPostLoginPath(user));
    } catch {
      setError('Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={title}
      subtitle={subtitle}
      hideStoreBranding={isBackoffice}
      footer={
        isBackoffice ? (
          <p className="mt-1">
            <Link to="/esqueci-senha" className="text-primary hover:underline">
              Esqueci minha senha
            </Link>
          </p>
        ) : (
          <>
            <p className="text-app-text-secondary">
              Não tem uma conta?{' '}
              <Link to="/cadastrar" className="text-primary hover:underline font-semibold">
                Cadastre-se
              </Link>
            </p>
            <p className="mt-1">
              <Link to="/esqueci-senha" className="text-primary hover:underline">
                Esqueci minha senha
              </Link>
            </p>
          </>
        )
      }
    >
      {reasonMessage && (
        <div className="bg-warning/10 border border-warning/30 text-warning px-4 py-3 rounded-lg mb-4 text-sm">
          {reasonMessage}
        </div>
      )}
      {/* Aviso quando MANAGER/REVITER acessa /entrar do whitelabel.
          Doc Seção 10: estes papéis devem usar o backoffice, não o storefront. */}
      {mode === 'public' && currentStore && (isReviterAdmin(currentUser) || isStoreManager(currentUser)) && (
        <div className="bg-info/10 border border-info/30 text-info px-4 py-3 rounded-lg mb-4 text-sm">
          <p className="font-semibold">Você é administrador.</p>
          <p>
            Acesse o backoffice em{' '}
            <Link
              to={isReviterAdmin(currentUser) ? '/admin-reviter' : '/fornecedor/painel'}
              className="underline font-semibold"
            >
              {isReviterAdmin(currentUser) ? '/admin-reviter' : '/fornecedor/painel'}
            </Link>.
          </p>
        </div>
      )}
      {error && (
        <div className="bg-error/10 border border-error/30 text-error px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Email, CPF ou celular"
          name="email"
          type="text"
          value={formData.email}
          onChange={handleChange}
          required
          placeholder="seu@email.com"
          autoComplete="username"
          leftIcon={<Mail size={18} />}
        />
        <Input
          label="Senha"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange}
          required
          placeholder="••••••••"
          autoComplete="current-password"
          leftIcon={<Lock size={18} />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="hover:text-app-text transition-colors pointer-events-auto"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />
        <Button type="submit" fullWidth loading={loading} size="lg">
          Entrar
        </Button>
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
